import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Pencil } from '@factorialco/f0-react/icons/app'
import { LoadContentDocument, ProcessContentDocument } from 'generated/resources/graphql'
import { isEqual } from 'lodash'
import { autorun } from 'mobx'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCocreationAgent } from 'modules/ai_reports/lib/useCocreationAgent'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { LMSModuleAvailabilityWall } from 'modules/trainings/components/LMSModuleAvailabilityWall'
import ContentBuilder from 'modules/trainings/components/content/Builder'
import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { ContentPreview } from 'modules/trainings/components/content/preview/ContentPreview'
import { ContentItem } from 'modules/trainings/lib/content/ContentItem'
import { ModuleCollection } from 'modules/trainings/lib/content/ModuleCollection'
import {
  ContentAgentStateType,
  contentFromAi,
  contentToAi,
} from 'modules/trainings/lib/content/aiTypes'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

interface BuilderProps {
  /**
   * The collection containing the modules, their blocks, and associated configurations.
   */
  collection: ModuleCollection
  /**
   * Indicates whether there are unsaved changes in the current state.
   */
  hasUnsavedChanges: boolean
  /**
   * Function to update the state when unsaved changes occur.
   */
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void
  /**
   * Callback to save the content
   */
  onSave: () => void
  /**
   * Callback to discard changes and reload the content from the server.
   */
  onDiscard: () => void
}

/**
 * Component that renders the LMS content builder user interface.
 * It provides functionality for managing and organizing content modules and blocks within a collection.
 */
const ContentBuilderComponent = observer(
  ({ collection, hasUnsavedChanges, setHasUnsavedChanges, onSave, onDiscard }: BuilderProps) => {
    return (
      <ContentBuilder
        tocItems={collection.modules.map((contentModule) => ({
          id: contentModule.uuid,
          icon: contentRegistry.fetch(contentModule, 'icon'),
          label: contentModule.title,
          onClick: () => collection.setSelected(contentModule.uuid),
          children: contentModule.blocks.map((block) => ({
            id: block.uuid,
            label: block.title,
            icon: contentRegistry.fetch(block, 'icon'),
            onClick: () => collection.setSelected(block.uuid),
          })),
        }))}
        selectedItem={collection.selectedItem}
        trainingId={collection.trainingId}
        hasUnsavedChanges={hasUnsavedChanges}
        onReorder={(orderedStructure) => {
          collection.reorder(orderedStructure)
          setHasUnsavedChanges(true)
        }}
        onAddContent={({ contentItem }: { contentItem: ContentItem }) => {
          collection.addItem(contentItem)
          setHasUnsavedChanges(true)
        }}
        onDeleteContent={(uuid: string) => {
          collection.deleteItem(uuid)
          setHasUnsavedChanges(true)
        }}
        onContentChange={() => {
          setHasUnsavedChanges(true)
        }}
        onSave={onSave}
        onDiscard={onDiscard}
      />
    )
  }
)

/**
 * Component that integrates content creation functionalities with an AI-driven agent.
 * It manages the content-building process and interacts with the agent
 * to suggest, synchronize, and update content based on a given collection.
 *
 * The agent is initialized with specific configurations, such as its name, initial state,
 * and welcome messages. The state of the agent is monitored and updated based on changes
 * in the content collection, ensuring consistency between the local content and the agent's
 * internal state. If the agent is disabled, related operations gracefully handle the absence
 * of agent-specific functionality.
 *
 * A reactive approach is implemented using hooks, such as `useEffect` and `autorun`,
 * to synchronize the state effectively without manual interventions:
 * - Updates content from the AI agent to the local collection.
 * - Propagates significant changes in the collection back to the agent.
 *
 * The `ContentBuilderComponent` is rendered to encapsulate the interface for content creation,
 * accommodating both agent-driven interactions and manual adjustments.

 */
const ContentBuilderWithAgent = observer(
  ({ collection, hasUnsavedChanges, setHasUnsavedChanges, onSave, onDiscard }: BuilderProps) => {
    const { state: agentState, setState: setAgentState } = useCocreationAgent<
      ContentAgentStateType,
      ContentAgentStateType
    >(
      {
        name: 'training-content-workflow',
        initialState: { content: contentToAi(collection), updateContent: false },
        initialMessage: i18n.t('trainings.one.initial_message'),
        welcomeScreenSuggestions: [
          {
            icon: Pencil,
            message: i18n.t('trainings.one.welcome_suggestions.create_new_content.message'),
            prompt: i18n.t('trainings.one.welcome_suggestions.create_new_content.prompt'),
          },
          {
            icon: Pencil,
            message: i18n.t('trainings.one.welcome_suggestions.create_new_module.message'),
            prompt: i18n.t('trainings.one.welcome_suggestions.create_new_module.prompt'),
          },
        ],
      },
      true
    )

    // update the local state when agent state has changed
    useEffect(() => {
      // if agent is disabled, 'agentState' will be undefined.
      if (agentState && collection && agentState.updateContent) {
        contentFromAi(collection, agentState.content)
      }
    }, [agentState, collection])

    // update the agent state when local state has changed
    autorun(() => {
      // if agent is disabled, 'setAgentState' will be undefined.
      if (setAgentState && collection) {
        const newState = contentToAi(collection)
        if (!isEqual(newState, agentState?.content)) {
          setAgentState({ content: newState, updateContent: false })
        }
      }
    })

    return (
      <ContentBuilderComponent
        collection={collection}
        hasUnsavedChanges={hasUnsavedChanges}
        setHasUnsavedChanges={setHasUnsavedChanges}
        onSave={onSave}
        onDiscard={onDiscard}
      />
    )
  }
)

const ContentBuilderPage = observer(() => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { id: trainingId } = useParams()
  const session = useCurrentSession()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isPreviewMode, setIsPreviewMode] = useState(false)

  // Load content modules for the current training.
  const { data, loading, refetch } = useQuery(LoadContentDocument, {
    variables: {
      trainingId: Number(trainingId),
    },
  })

  // Check if the user has the required features to use the AI-driven content builder./
  const { has_dev_ai_agent, has_dev_training_lms_ai, loadingFeatures } = useFeatures([
    'dev_ai_agent',
    'dev_training_lms_ai',
  ])

  // Save the content modules for the current training.
  const [processContent, { loading: isSaving }] = useMutation(ProcessContentDocument, {
    extractorFn(mutationData) {
      return mutationData.trainingsMutations.processContentTrainingsTraining
    },
  })

  // Convert the content modules from GraphQL to a ModuleCollection, which is used by the builder.
  const collection = useMemo(() => {
    const modules = data?.trainings.contentModules
    if (loading || !modules) {
      return undefined
    }
    return graphqlContentMapper.fromGraphQL(modules, Number(trainingId))
  }, [trainingId, data, loading])

  if (!collection || loading || loadingFeatures) {
    return <Loading expand />
  }

  const handleSave = () => {
    if (!isSaving) {
      // Use GraphQL mapper to convert from domain to GraphQL input
      const contentModules = graphqlContentMapper.toGraphQL(collection)

      processContent({
        variables: {
          authorId: session.employee.id,
          companyId: session.company.id,
          trainingId: collection.trainingId,
          contentModules,
        },
      }).then((result) => {
        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.content.save.error'),
          })
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('trainings.content.save.success'),
          })
          setHasUnsavedChanges(false)
        }
      })
    }
  }

  const handleDiscard = () => {
    refetch()
    setHasUnsavedChanges(false)
  }

  // If the user does not have the required features, render the builder without the agent.
  const Component =
    has_dev_ai_agent && has_dev_training_lms_ai ? ContentBuilderWithAgent : ContentBuilderComponent

  return (
    <LMSModuleAvailabilityWall>
      {isPreviewMode && (
        <ContentPreview collection={collection} onClose={() => setIsPreviewMode(false)} />
      )}
      <ResponsivePage.Body
        variant="fullscreen"
        height="full"
        onActionClick={(action: string) => {
          if (action === 'preview') {
            setIsPreviewMode(true)
          } else if (action === 'back') {
            return navigate(
              navigation.trainings.list.details_revamp.view.overview.fullPath({
                id: Number(trainingId),
              })
            )
          }
        }}
      >
        <Component
          collection={collection}
          hasUnsavedChanges={hasUnsavedChanges}
          setHasUnsavedChanges={setHasUnsavedChanges}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </ResponsivePage.Body>
    </LMSModuleAvailabilityWall>
  )
})

export default ContentBuilderPage
